import React, { JSX, useMemo, useState } from "react";
import { getCustomerTierOverview } from "../../datasource/CustomerTierProgramDatasource";
import { message } from "antd";
import { DownloadOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitle from "../../components/PageTitle/PageTitle";
import AntdTabs from "../../components/AntdTabs/AntdTabs";
import Text from "../../components/Text/Text";
import Button from "../../components/Button/Button";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { resolveShopTierConfig } from "./config";
import { generateShopTierMockData } from "./mockData";
import ShopListTab from "./ShopListTab";
import AuditLogTab from "./AuditLogTab";
import ConfigTab from "./ConfigTab";
import SearchInput from "../../components/Input/SearchInput";
import Select from "../../components/Select/Select";
import {
  KpiRow,
  KpiChip,
  KpiValue,
  KpiUnit,
  KpiTitle,
  KpiSub,
  ToolbarRow,
  NavWarningBanner,
  NavWarningLink,
  ClearFilterRow,
} from "./styled";
import { useNavigate } from "react-router";
import { useQuery } from "react-query";
import { zoneDatasource } from "../../datasource/ZoneDatasource";
import color from "../../resource/color";

interface TierLevel {
  tierLevelId: string;
  tierName: string;
  tierRank: number;
  color: string;
  minTarget: number;
  maxTarget: number | null;
  customerCount: number;
}

function ShopTierPage(): JSX.Element {
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const mockData = useMemo(() => generateShopTierMockData(config), [config]);
  const [selectedTab, setSelectedTab] = useState("history");
  const [search, setSearch] = useState("");
  const [isNavPending, setIsNavPending] = useState(false);
  const [zone, setZone] = useState<string | null>();
  const [year, setYear] = useState<string>('ทุกปี');
  const [action, setAction] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");
  const navigate = useNavigate();
  const [tierFilter, setTierFilter] = useState<string | null>(null);

  // TODO: ยังเป็น placeholder อยู่ รอ API นับจำนวนร้านที่รอคีย์เข้า Navision
  const hasActiveFilter = !!tierFilter || !!search || !!zone || status !== "ALL";
  const handleClearFilters = () => {
    setTierFilter(null);
  };

  const { data : customerTierOverview, isLoading: isCustomerTierOverviewLoading } = useQuery(
    ["tier-available-year", company?.company],
    () => getCustomerTierOverview({ company: company?.company }),
    { enabled: !!company, refetchOnWindowFocus: false },
  );

  const pendingNavCount = customerTierOverview?.responseData?.pendingNavCount || 0;
  const tierLevels: TierLevel[] = customerTierOverview?.responseData?.tierLevels || [];

  const yearOptions = useMemo(() => {
    return ['ทุกปี','ปี 2569','ปี 2568','ปี 2567'].map((y) => ({ key: y, value: y, label: `${y}` }));
  }, [mockData.configYears]);

  const actionOptions = useMemo(() => {
    const actions = Array.from(new Set(mockData.auditLog.map((a) => a.action)));
    return [{ key: "all", value: "all", label: "ทุกการดำเนินการ" }, ...actions.map((a) => ({ key: a, value: a, label: a }))];
  }, [mockData.auditLog]);

  const { data: zones, isLoading: isZonesLoading } = useQuery(
    ["zones-by-company", company?.company],
    async() => {
      const data = await zoneDatasource.getAllZoneByCompany(company?.company);
      return data.map((res)=>({ key: res.zoneId, value: res.zoneName, label: res.zoneName }));
    },
    { enabled: !!company?.company, refetchOnWindowFocus: false }
  );

  const statusOptions = useMemo(() => {
    return [{ key: "ALL", value: "ALL", label: "สถานะทั้งหมด" }, { key: "PENDING_REVIEW", value: "PENDING_REVIEW", label: "รอพิจารณาปรับระดับ" }];
  }, [mockData.configYears]);
  const tabsData = [
    {
      key: "history",
      label: "ร้านค้าทั้งหมด",
      children: (
        <ShopListTab
          config={config}
          search={search}
          zone={zone}
          tierFilter={tierFilter}
          status={status}
        />
      ),
    },
    {
      key: "audit",
      label: "Audit Log",
      children: <AuditLogTab auditLog={mockData.auditLog} />,
    },
    {
      key: "config",
      label: "ตั้งค่าระดับ",
      children: <ConfigTab config={config} configYears={mockData.configYears} />,
    },
  ];
  
  const rangeTier = (minTarget: number | null, maxTarget: number | null) => {
    if(company.company === "ICPL"){
      if (minTarget !== null && maxTarget !== null) {
        return `${(minTarget / 1000000).toFixed(0)}-${(maxTarget / 1000000).toFixed(0)}M฿`;
      } else if (minTarget !== null) {
        return `${(minTarget / 1000000).toFixed(0)}M฿ ขึ้นไป`;
      } else if (maxTarget !== null) {
        return `ไม่เกิน ${(maxTarget / 1000000).toFixed(0)}M฿`;
      } else {
        return "-";
      }
    }
    if (minTarget !== null && maxTarget !== null) {
      return `${minTarget} - ${maxTarget} ตัน`;
    } else if (minTarget !== null) {
      return `ตั้งแต่ ${minTarget} ตัน`;
    } else if (maxTarget !== null) {
      return `ไม่เกิน ${maxTarget} ตัน`;
    } else {
      return "-";
    }
  };

  return (
    <CardContainer>
      <PageTitle
        title="ระดับร้านค้า"
        extraSpan={8}
        extra={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {
              selectedTab === "history" && (
                <Button
                  typeButton="border-primary"
                  style={{ width: "auto" }}
                  icon={<DownloadOutlined />}
                  title="Export ข้อมูลระดับร้านค้า"
                  onClick={() => message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา")}
                />
              )
            }
            {
              selectedTab === "audit" && (
                <Button
                  typeButton="border-primary"
                  style={{ width: "auto" }}
                  icon={<DownloadOutlined />}
                  title="Export Audit Log"
                  onClick={() => message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา")}
                />
              )
            }
            {
              selectedTab === "config" && (
                <Button
                  typeButton="primary"
                  style={{ width: "auto" }}
                  icon={<PlusOutlined />}
                  title="สร้างปีสิทธิประโยชน์"
                  onClick={() => navigate("/ShopTier/create/benefit")}
                />
              )
            }
          </div>
        }
      />
      <div style={{ marginTop: 4, marginBottom: 16 }}>
        <Text fontSize={13} color="Text3">
          อัปเดตล่าสุดเมื่อ 21/07/2569, 09:14
        </Text>
      </div>
      {
        selectedTab === "history" && (
          <KpiRow aria-label={`จำนวนร้านต่อระดับ ${config.companyLabel}`}>
            {tierLevels.map((tier) => (
              <KpiChip
                key={tier.tierLevelId}
                type="button"
                $bg={tier.color}
                $active={tierFilter === tier.tierLevelId}
                onClick={() => setTierFilter((prev) => (prev === tier.tierLevelId ? null : tier.tierLevelId))}
              >
                <KpiValue>
                  {tier.customerCount || 0}
                  <KpiUnit>ร้าน</KpiUnit>
                </KpiValue>
                <KpiTitle>{tier.tierName}</KpiTitle>
                <KpiSub>{rangeTier(tier.minTarget, tier.maxTarget)}</KpiSub>
              </KpiChip>
            ))}
          </KpiRow>
        )
      }

      {
        selectedTab === "history" && (
          <NavWarningBanner>
            <WarningOutlined style={{ color: color.warning, fontSize: 20 }} />
            <Text fontSize={14} fontFamily="Sarabun" color="Text1">
              มี {pendingNavCount} ร้าน ที่เปลี่ยนระดับแล้วยังไม่ได้คีย์เข้า Navision ให้คีย์ในระบบ Navision ก่อน
              แล้วจึงกลับมาทำเครื่องหมายในรายการ{" "}
              <NavWarningLink onClick={() => setIsNavPending(!isNavPending)} style={{ textDecoration: "underline", cursor: "pointer", fontFamily: "Sarabun" }}>{isNavPending ? "ปิดตัวกรอง" : "กรองดูร้านค้า"}</NavWarningLink>
            </Text>
          </NavWarningBanner>
        )
      }

      {
        selectedTab === "history" && tierFilter !== null && (
          <ClearFilterRow>
            <Button
              typeButton="border-primary"
              style={{ width: "auto", borderRadius: 20 }}
              title="ล้างตัวกรอง"
              onClick={handleClearFilters}
            />
          </ClearFilterRow>
        )
      }

      <ToolbarRow style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        {
          (selectedTab === "history" || selectedTab === "audit") && <SearchInput
            style={{ width: 320 }}
            placeholder="ค้นหารหัสร้านค้า / ชื่อร้านค้า"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
        {
          selectedTab === "history" && (
            <div style={{display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 180, marginRight: 8 }}>
                <Select fontSize={14} data={zones} value={zone!} onChange={(v) => setZone(v)} placeholder="เขตทั้งหมด" />
              </div>
              <div style={{ width: 180 }}>
                <Select fontSize={14} data={statusOptions} value={status} onChange={(v) => setStatus(v)} />
              </div>
            </div>
          )
        }
        {
          selectedTab === "audit" && (
            <div style={{display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 180, marginRight: 8 }}>
                <Select fontSize={14} data={zones} value={zone!} onChange={(v) => setZone(v)} style={{ marginRight: 8 }} />
              </div>
              <div style={{ width: 120, marginRight: 8 }}>
                <Select fontSize={14} data={yearOptions} value={year} onChange={(v) => setYear(v)} />
              </div>
              <div style={{ width: 250 }}>
                <Select fontSize={14} data={actionOptions} value={action} onChange={(v) => setAction(v)} />
              </div>
            </div>
          )
        }
      </ToolbarRow>
      <AntdTabs data={tabsData} defaultTab={selectedTab} onChange={(v) => setSelectedTab(v)} />
    </CardContainer>
  );
}

export default ShopTierPage;
